import {Button} from '@/components/MLS/Button/Button';
import {MAX_HEIC_CONVERSION_DIMENSION} from '@/constants/avatarCreator';
import {ChangeEvent, useRef, useState} from 'react';
import {toast} from 'react-toastify';
import Webcam from 'react-webcam';
import {convertHeicToJpeg, isHeicFile, showHeicGuidance} from './heic';

const MAX_FILE_SIZE_MBS = 10;

interface PhotoSourceSelectorProps {
  photoPresent: boolean;
  onPhotoSelect: (dataUrl: string) => void;
}

const PhotoSourceSelector = ({photoPresent, onPhotoSelect}: PhotoSourceSelectorProps) => {
  const [cameraActive, setCameraActive] = useState(false);
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The upload button stays interactive while handleFileUpload's async work (HEIC detection,
  // conversion) is in flight, so a second selection can start before the first resolves. Track
  // the latest request and drop results from any earlier one.
  const latestUploadIdRef = useRef(0);

  const handleCapture = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      // Extract the base64-encoded part of the data URL.
      const base64Data = imageSrc.split(',')[1];
      // Decode the base64 string to get the binary data length in bytes.
      const imageByteSize = atob(base64Data).length;
      if (imageByteSize > MAX_FILE_SIZE_MBS * 1024 * 1024) {
        setCameraActive(false);
        toast.error(`Photo exceeds the maximum allowed limit of ${MAX_FILE_SIZE_MBS}MBs.`);
        return;
      }
      onPhotoSelect(imageSrc);
      setCameraActive(false);
    }
  };

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const uploadId = ++latestUploadIdRef.current;
    const isStale = () => uploadId !== latestUploadIdRef.current;

    console.log('file.type', file.type);
    const heic = await isHeicFile(file);
    if (isStale()) {
      return;
    }

    // Validate file is an image before proceeding. HEIC's reported MIME type is inconsistent
    // across platforms, so don't reject a real HEIC file based on `file.type`.
    if (!heic && !file.type.startsWith('image/')) {
      toast.error('Please upload only images.');
      return;
    }
    // Check if the file size exceeds 5MB
    if (file.size > MAX_FILE_SIZE_MBS * 1024 * 1024) {
      toast.error(`Photo exceeds the maximum allowed limit of ${MAX_FILE_SIZE_MBS}MBs.`);
      return;
    }

    let uploadFile = file;
    if (heic) {
      const converted = await convertHeicToJpeg(file, MAX_HEIC_CONVERSION_DIMENSION);
      if (isStale()) {
        return;
      }
      if (!converted) {
        showHeicGuidance();
        return;
      }
      // The downscale cap makes this unlikely, but re-check the limit on the converted
      // output too, since it's what actually gets uploaded.
      if (converted.size > MAX_FILE_SIZE_MBS * 1024 * 1024) {
        toast.error(`Photo exceeds the maximum allowed limit of ${MAX_FILE_SIZE_MBS}MBs.`);
        return;
      }
      uploadFile = converted;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      if (isStale()) {
        return;
      }
      if (event.target?.result) {
        onPhotoSelect(event.target.result as string);
      }
    };
    reader.readAsDataURL(uploadFile);
  };

  return (
    <div
      data-test-id="avatar-creator-photo-source-selector"
      className="flex flex-col items-center justify-center gap-6"
    >
      <h2 className="mt-4 px-4 text-center text-sm text-mls-grey-xdark xs:text-base">
        Upload or take a photo to create your avatar
      </h2>
      <div className="flex flex-col gap-4">
        {cameraActive ? (
          <div className="fixed left-0 top-0 z-100 flex h-full w-full flex-col items-center bg-black">
            <Webcam
              data-test-id="avatar-creator-photo-source-selector-webcam"
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={{facingMode: 'user'}}
              className="h-full w-full"
            />
            <div className="absolute bottom-12 mb-4 flex flex-col gap-4">
              <Button
                data-test-id="avatar-creator-photo-source-selector-capture-photo-button"
                label="Capture Photo"
                onClick={handleCapture}
                className="2xs:text-base xs:text-lg"
              />
              <Button
                data-test-id="avatar-creator-photo-source-selector-cancel-button"
                label="Cancel"
                onClick={() => setCameraActive(false)}
                className="2xs:text-base xs:text-lg"
              />
            </div>
          </div>
        ) : (
          <div className="mb-4 flex flex-col gap-4">
            <Button
              data-test-id="avatar-creator-photo-source-selector-take-photo-button"
              variant="dark"
              label="Take Photo"
              onClick={() => setCameraActive(true)}
              className="2xs:text-sm xs:text-lg"
            />

            <input type="file" accept="image/*" onChange={handleFileUpload} ref={fileInputRef} hidden />
            <Button
              data-test-id="avatar-creator-photo-source-selector-upload-photo-button"
              variant="dark"
              label={photoPresent ? 'Upload new Photo' : 'Upload Photo'}
              onClick={() => fileInputRef.current?.click()}
              className="2xs:text-sm xs:text-lg"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoSourceSelector;
